import type { TFunction } from "i18next";
import { ITEM_KINDS } from "../../../api/content/reviewDecoders";
import type { PlannedInput } from "../../../api/content/stepPlans";
import type { ItemAtVersion } from "../../threads/prepare";
import { editTarget } from "../graph/editTarget";
import { sourceItemKind } from "../model/graphSources";
import type { SourceGraphNode } from "../model/workflowGraph";
import type { ItemFacts, ItemOrigin } from "./itemFacts";
import { itemTypeText, typesHint, typesText } from "./typeWords";
import { lineageOf } from "../../../api/content/typeLineage";
import { resolvePresentation } from "../presentation/resolvePresentation";

/**
 * One input card's facts (F61, F56), built here once for both places that show inputs: the
 * Workflow graph's input band and a step's inputs in a Thread. Each card shows its type in
 * DMW's ontology, never "Item" or "Type not declared"; its readable version (ADR-0033),
 * never a hash; whether it was made by hand or by a Workflow; and Edit, which opens the
 * edit Thread on the item (F51, F36). The graph lays the facts out on its fixed-size cards,
 * a step on `InputCard`.
 */
export interface InputCardFacts {
  /** Its type in words, such as "Label list" or "Several: Competency question". */
  readonly type: string;
  /** Its type's hint, such as "A whole number." (`typesHint`); `null` or absent where it has none. */
  readonly typeHint?: string | null;
  /** Such as `1.2.0`; `null` where the item has none. */
  readonly version: string | null;
  /** "Made by hand" or "Made by ‹Workflow›"; `null` where DMW doesn't record it. */
  readonly origin: string | null;
  /** The item Edit opens the edit Thread on; `null`: no Edit. */
  readonly edit: ItemAtVersion | null;
  /**
   * "Shown as Document; only Annual report fits": a type without a view of its own is shown
   * with its nearest ancestor's (ADR-0032 D8), and still accepts only itself; `null` otherwise.
   */
  readonly shownAs?: string | null;
}

/** A source in the graph's input band, with the item Edit opens (`editTarget`). */
export function sourceCardFacts(node: SourceGraphNode, title: string, t: TFunction): InputCardFacts {
  return { type: itemTypeText(sourceItemKind(node), t), version: node.version, origin: originOf(sourceOrigin(node), t), edit: editTarget(node, title) };
}

/**
 * A step's input from its plan (ADR-0032 D7): the item it holds, prefilled or picked, as
 * the graph shows a source (its class, version, origin and Edit); else its declared types.
 */
export function plannedCardFacts(input: PlannedInput, item: ItemFacts | null, t: TFunction): InputCardFacts {
  const shownAs = shownAsText(input, t);
  if (item === null) return { type: typesText(input.accepts, t), typeHint: typesHint(input.accepts, t), version: null, origin: null, edit: null, shownAs };
  const kind = ITEM_KINDS.find((candidate) => candidate === item.kind);
  return {
    type: itemTypeText({ kind: item.kind, many: false }, t),
    version: item.version,
    origin: originOf(item.origin, t),
    edit: kind === undefined ? null : { itemKind: kind, name: item.name, reference: item.reference },
    shownAs,
  };
}

/** Which ancestor's view shows the input's first type, where it has none of its own. */
function shownAsText(input: PlannedInput, t: TFunction): string | null {
  const [first] = input.accepts;
  if (first === undefined) return null;
  const operation = input.takesValue && !input.takesItem ? "edit" : "inspect";
  const resolved = resolvePresentation(lineageOf(first, input.lineages), operation);
  if (resolved === null || resolved.via === "own") return null;
  const view = typesText([{ ...first, type: resolved.presentation.target, many: false }], t);
  return t("workflows.inputCard.shownAs", { view, type: typesText([{ ...first, many: false }], t) });
}

/** "Version 1.2.0 · Made by hand": the line a card shows under its name; `null` when it knows neither. */
export function inputCardMeta(facts: Pick<InputCardFacts, "version" | "origin">, t: TFunction): string | null {
  const parts = [facts.version === null ? null : t("workflows.inputCard.version", { version: facts.version }), facts.origin].filter((part) => part !== null);
  return parts.length === 0 ? null : parts.join(" · ");
}

/** "Made by hand" or "Made by ‹Workflow›". */
export function originText(origin: ItemOrigin, t: TFunction): string {
  return origin.kind === "hand" ? t("workflows.node.madeByHand") : t("workflows.node.madeBy", { workflow: origin.name });
}

function originOf(origin: ItemOrigin | null, t: TFunction): string | null {
  return origin === null ? null : originText(origin, t);
}

/** Only an artifact records what made its version: a Workflow run, or a person by hand. */
function sourceOrigin(node: SourceGraphNode): ItemOrigin | null {
  if (node.kind !== "artifact") return null;
  const { madeBy } = node.artifact;
  // A Workflow that only proposes the artifact (`declared`) did not make this version: it opens in place.
  return madeBy === null ? { kind: "hand" } : { kind: "workflow", name: node.producer?.name ?? madeBy.workflow };
}
